import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, Typography } from "@mui/material";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import {
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    GroupRounded,
    CalendarMonthRounded,
    TrendingUpRounded,
} from "@mui/icons-material";
import utils from "../../universal/utils";
import { buildReferralInviteLink } from "../../universal/referralLinks";
import { pushToast } from "../../stores/toastStore";
import { colors } from "../../theme/colors";
import { TIERS, TIER_DISPLAY } from "../../constants/membership";
import {
    ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_ARROW_SX,
    ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX,
    ACCOUNT_PAGE_SUBSECTION_SX,
    ACCOUNT_PAGE_SUBSECTION_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();

const DAY_MS = 24 * 60 * 60 * 1000;

const SUBSECTION_COLLAPSE_TIMEOUT = {
    enter: 320,
    exit: 260,
};

const SUBSECTION_COLLAPSE_EASING = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
};

/* ---------- shared styling tokens (Ouro settings-page coherent) ---------- */

const REFERRAL_SUMMARY_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "stretch" },
    width: "100%",
    mt: "1.75rem",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: `
        linear-gradient(
            165deg,
            rgba(18,18,22,0.92) 0%,
            rgba(12,12,14,0.96) 52%,
            rgba(10,10,12,0.98) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
};

const REFERRAL_SUMMARY_ZONE_SX = {
    flex: 1,
    minWidth: 0,
    px: "1.35rem",
    py: "1.1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.55rem",
};

const REFERRAL_SUMMARY_DIVIDER_SX = {
    display: { xs: "none", sm: "block" },
    alignSelf: "stretch",
    width: "1px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.06)",
};

const REFERRAL_SUMMARY_DIVIDER_HORIZONTAL_SX = {
    display: { xs: "block", sm: "none" },
    width: "100%",
    height: "1px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.06)",
};

const REFERRAL_SUMMARY_LABEL_SX = {
    color: colors.text.muted,
    fontSize: "0.68rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
};

const REFERRAL_ZONE_ICON_SX = {
    width: "1.9rem",
    height: "1.9rem",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    background: `linear-gradient(135deg, ${colors.accent.subtle}, rgba(232,163,61,0.04))`,
    border: "1px solid rgba(232,163,61,0.16)",
    color: colors.accent.main,
    "& svg": { fontSize: "1rem" },
};

const REFERRAL_ZONE_NUMBER_SX = {
    color: colors.text.primary,
    fontSize: "1.9rem",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
};

const REFERRAL_TREND_TAG_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    alignSelf: "flex-start",
    px: "0.65rem",
    py: "0.28rem",
    borderRadius: "999px",
    background: "rgba(34,197,94,0.14)",
    border: "1px solid rgba(34,197,94,0.28)",
    color: colors.state.success.main,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    "& svg": { fontSize: "0.85rem" },
};

const REFERRAL_ZONE_CAPTION_SX = {
    color: colors.text.muted,
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: "0.01em",
};

const REFERRAL_FIELD_BLOCK_SX = {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
    py: "0.9rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-of-type": { borderBottom: "none" },
};

const REFERRAL_FIELD_LINE_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    width: "100%",
};

const REFERRAL_FIELD_BOX_SX = {
    flex: 1,
    minWidth: 0,
    px: "0.85rem",
    py: "0.7rem",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.03)",
    overflow: "hidden",
};

const REFERRAL_STAT_ROW_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    py: "0.85rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-of-type": { borderBottom: "none" },
};

const REFERRAL_STAT_VALUE_SX = {
    color: colors.text.primary,
    fontSize: "1.05rem",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
};

const REFERRAL_TABLE_HEAD_CELL_SX = {
    color: colors.text.muted,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    paddingBottom: "0.55rem",
};

const REFERRAL_TABLE_ROW_SX = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    py: "0.8rem",
    borderTop: "1px solid rgba(255,255,255,0.05)",
};

const REFERRAL_INVITE_CHIP_BASE_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    px: "0.6rem",
    py: "0.26rem",
    borderRadius: "999px",
    border: "1px solid",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    "& .dot": {
        width: "5px",
        height: "5px",
        borderRadius: "50%",
        backgroundColor: "currentColor",
    },
};

const REFERRAL_STEP_ROW_SX = {
    display: "flex",
    gap: "1rem",
    py: "0.95rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-of-type": { borderBottom: "none" },
};

/* ---------- helpers ---------- */

function formatRelativeTime(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    const diffDays = Math.floor((Date.now() - date.getTime()) / DAY_MS);
    if (diffDays <= 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    }
    const months = Math.floor(diffDays / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
}

function getInviteStatus(row) {
    if (row?.credited || row?.status === "paid" || row?.status === "credited") {
        return { key: "subscribed", label: "Subscribed" };
    }
    return { key: "pending", label: "Pending" };
}

function getDaysUntilRenewal(membership) {
    if (membership?.expiresAt) {
        const expiry = new Date(membership.expiresAt);
        if (!Number.isNaN(expiry.getTime())) {
            const days = Math.ceil((expiry.getTime() - Date.now()) / DAY_MS);
            return days > 0 ? days : 0;
        }
    }

    const daysRemaining = Number(membership?.daysRemaining);
    return membership?.daysRemaining != null && Number.isFinite(daysRemaining)
        ? Math.max(0, Math.ceil(daysRemaining))
        : null;
}

/* ---------- chart tooltip (matches EloProgressionChart style) ---------- */

const ReferralTrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    return (
        <Box
            sx={{
                backgroundColor: "#1C1E20",
                border: "1px solid rgba(232, 163, 61, 0.3)",
                borderRadius: "4px",
                padding: "0.75rem",
            }}
        >
            <Typography sx={{ fontSize: "0.85rem", color: "#ffffff", fontWeight: 600 }}>
                {data.nickname}
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#E8A33D", fontWeight: 700 }}>
                Total: {data.days} days earned
            </Typography>
        </Box>
    );
};

ReferralTrendTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object),
};

/* ---------- collapsible section (same behavior as Account page) ---------- */

function ReferralSection({ id, title, children }) {
    const [open, setOpen] = useState(true);
    const contentId = `${id}-content`;
    const ArrowIcon = open ? KeyboardArrowUpRounded : KeyboardArrowDownRounded;

    return (
        <Box sx={ACCOUNT_PAGE_SUBSECTION_SX}>
            <Typography component="h2" sx={{ m: 0, p: 0 }}>
                <Box
                    component="button"
                    type="button"
                    aria-expanded={open}
                    aria-controls={contentId}
                    onClick={() => setOpen((current) => !current)}
                    sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                >
                    <Box component="span" sx={ACCOUNT_PAGE_SUBSECTION_TITLE_SX}>
                        {title}
                    </Box>
                    <ArrowIcon aria-hidden sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX} />
                </Box>
            </Typography>
            <Collapse
                in={open}
                timeout={SUBSECTION_COLLAPSE_TIMEOUT}
                easing={SUBSECTION_COLLAPSE_EASING}
            >
                <Box id={contentId}>{children}</Box>
            </Collapse>
            <Box sx={ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX} />
        </Box>
    );
}

ReferralSection.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
};

/* ---------- main page ---------- */

function ReferralProgramPage({ membership }) {
    const [stats, setStats] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadReferralData() {
            setLoading(true);
            setLoadError(false);
            try {
                const [statsResponse, historyResponse] = await Promise.all([
                    fetch(`${basePath}/api/v1/referral/my-code`, { credentials: "include" }),
                    fetch(`${basePath}/api/v1/referral/history`, { credentials: "include" }),
                ]);
                const statsData = await statsResponse.json().catch(() => null);
                const historyData = await historyResponse.json().catch(() => null);
                if (cancelled) return;
                if (statsData?.ok) setStats(statsData);
                if (historyData?.ok && Array.isArray(historyData.history)) {
                    setHistory(historyData.history);
                }
            } catch {
                if (!cancelled) setLoadError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadReferralData();
        return () => {
            cancelled = true;
        };
    }, []);

    const referralCode = stats?.code || stats?.referralCode || "";
    const inviteLink = referralCode ? buildReferralInviteLink(referralCode) : "";

    const friendsInvited = stats?.friendsJoined ?? stats?.totalReferrals ?? 0;
    const friendsSubscribed =
        stats?.friendsSubscribed ?? stats?.credited ?? 0;
    const daysEarned = stats?.daysEarned ?? stats?.totalDaysEarned ?? 0;
    const daysUntilRenewal = useMemo(
        () => getDaysUntilRenewal(membership),
        [membership],
    );
    const membershipPlanName = TIER_DISPLAY[membership?.tier]?.name || "Membership";
    const hasPaidMembership =
        membership?.tier === TIERS.PREMIUM ||
        membership?.tier === TIERS.PRO;

    const invitesThisMonth = useMemo(() => {
        const cutoff = Date.now() - 30 * DAY_MS;
        return history.filter((row) => {
            const timestamp = new Date(row.referredAt).getTime();
            return !Number.isNaN(timestamp) && timestamp >= cutoff;
        }).length;
    }, [history]);

    /* Real data only: cumulative days earned from credited referrals.
       The Trend section is hidden entirely when there is nothing to show. */
    const trendData = useMemo(() => {
        const creditedRows = history
            .filter((row) => row.credited && row.creditGrantedAt)
            .sort(
                (a, b) =>
                    new Date(a.creditGrantedAt).getTime() -
                    new Date(b.creditGrantedAt).getTime(),
            );
        let cumulative = 0;
        return creditedRows.map((row) => ({
            month: new Date(row.creditGrantedAt).toLocaleDateString("en-US", {
                month: "short",
            }),
            days: (cumulative += row.creditDays || 30),
            nickname: row.nickname,
        }));
    }, [history]);

    const handleCopy = (text, successMessage) => {
        navigator.clipboard.writeText(text).then(() => {
            pushToast({ type: "success", message: successMessage });
        }).catch(() => {
            pushToast({ type: "error", message: "Failed to copy — please try again." });
        });
    };

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Referral Program
                    </Typography>
                </Box>

                {/* Summary card */}
                <Box sx={REFERRAL_SUMMARY_SX} aria-label="Referral overview">
                    <Box sx={REFERRAL_SUMMARY_ZONE_SX}>
                        <Typography sx={REFERRAL_SUMMARY_LABEL_SX}>
                            Friends Invited
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <Box sx={REFERRAL_ZONE_ICON_SX}>
                                <GroupRounded aria-hidden />
                            </Box>
                            <Typography sx={REFERRAL_ZONE_NUMBER_SX}>
                                {loading ? "—" : friendsInvited}
                            </Typography>
                        </Box>
                        {invitesThisMonth > 0 ? (
                            <Box sx={REFERRAL_TREND_TAG_SX}>
                                <TrendingUpRounded aria-hidden />
                                +{invitesThisMonth} this month
                            </Box>
                        ) : (
                            <Typography sx={REFERRAL_ZONE_CAPTION_SX}>
                                No new invites this month
                            </Typography>
                        )}
                    </Box>

                    <Box aria-hidden sx={REFERRAL_SUMMARY_DIVIDER_HORIZONTAL_SX} />

                    <Box sx={REFERRAL_SUMMARY_ZONE_SX}>
                        <Typography sx={REFERRAL_SUMMARY_LABEL_SX}>
                            Days Earned
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <Box sx={REFERRAL_ZONE_ICON_SX}>
                                <CalendarMonthRounded aria-hidden />
                            </Box>
                            <Typography sx={REFERRAL_ZONE_NUMBER_SX}>
                                {loading ? "—" : daysEarned}
                            </Typography>
                        </Box>
                        <Typography sx={REFERRAL_ZONE_CAPTION_SX}>
                            Automatically applied to your subscription
                        </Typography>
                    </Box>

                    <Box aria-hidden sx={REFERRAL_SUMMARY_DIVIDER_SX} />
                </Box>

                {/* Your Referral Code */}
                <ReferralSection id="referral-code" title="Your referral code">
                    {loadError ? (
                        <Typography sx={{ ...REFERRAL_ZONE_CAPTION_SX, py: "0.9rem" }}>
                            Unable to load your referral code. Please try again later.
                        </Typography>
                    ) : (
                        <>
                            <Box sx={REFERRAL_FIELD_BLOCK_SX}>
                                <Typography component="label" htmlFor="referral-code-value" sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>
                                    Referral code
                                </Typography>
                                <Box sx={REFERRAL_FIELD_LINE_SX}>
                                    <Box id="referral-code-value" sx={REFERRAL_FIELD_BOX_SX}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                color: colors.text.primary,
                                                fontSize: "0.92rem",
                                                fontWeight: 600,
                                                letterSpacing: "0.08em",
                                            }}
                                        >
                                            {loading ? "…" : referralCode || "—"}
                                        </Typography>
                                    </Box>
                                    {referralCode ? (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={() => handleCopy(referralCode, "Referral code copied to clipboard")}
                                            sx={ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX}
                                        >
                                            Copy
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>

                            <Box sx={REFERRAL_FIELD_BLOCK_SX}>
                                <Typography component="label" htmlFor="referral-link-value" sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>
                                    Personal link
                                </Typography>
                                <Box sx={REFERRAL_FIELD_LINE_SX}>
                                    <Box id="referral-link-value" sx={REFERRAL_FIELD_BOX_SX}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                color: colors.text.secondary,
                                                fontSize: "0.82rem",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {loading ? "…" : inviteLink || "—"}
                                        </Typography>
                                    </Box>
                                    {inviteLink ? (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={() => handleCopy(inviteLink, "Invite link copied to clipboard")}
                                            sx={ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX}
                                        >
                                            Copy
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        </>
                    )}
                </ReferralSection>

                {/* Statistics */}
                <ReferralSection id="referral-statistics" title="Statistics">
                    <Box sx={{ pt: "0.25rem" }}>
                        <Box sx={REFERRAL_STAT_ROW_SX}>
                            <Typography sx={{ color: colors.text.secondary, fontSize: "0.85rem", fontWeight: 500 }}>
                                Friends invited
                            </Typography>
                            <Typography sx={REFERRAL_STAT_VALUE_SX}>
                                {loading ? "—" : friendsInvited}
                            </Typography>
                        </Box>
                        <Box sx={REFERRAL_STAT_ROW_SX}>
                            <Typography sx={{ color: colors.text.secondary, fontSize: "0.85rem", fontWeight: 500 }}>
                                Friends subscribed
                            </Typography>
                            <Typography sx={REFERRAL_STAT_VALUE_SX}>
                                {loading ? "—" : friendsSubscribed}
                            </Typography>
                        </Box>
                        <Box sx={REFERRAL_STAT_ROW_SX}>
                            <Typography sx={{ color: colors.text.secondary, fontSize: "0.85rem", fontWeight: 500 }}>
                                Total days earned
                            </Typography>
                            <Typography
                                sx={{
                                    ...REFERRAL_STAT_VALUE_SX,
                                    color: colors.accent.main,
                                }}
                            >
                                {loading ? "—" : daysEarned}
                                <Box
                                    component="span"
                                    sx={{
                                        ml: "0.25rem",
                                        color: colors.text.muted,
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                    }}
                                >
                                    days
                                </Box>
                            </Typography>
                        </Box>
                        {hasPaidMembership ? (
                            <Box sx={REFERRAL_STAT_ROW_SX}>
                                <Typography sx={{ color: colors.text.secondary, fontSize: "0.85rem", fontWeight: 500 }}>
                                    Days until {membershipPlanName} renewal
                                </Typography>
                                <Typography sx={REFERRAL_STAT_VALUE_SX}>
                                    {daysUntilRenewal ?? "—"}
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: "0.25rem",
                                            color: colors.text.muted,
                                            fontSize: "0.72rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        days
                                    </Box>
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </ReferralSection>

                {/* Trend — only rendered when real credited data exists */}
                {trendData.length > 0 ? (
                    <ReferralSection id="referral-trend" title="Trend">
                        <Box
                            sx={{
                                backgroundColor: "#1C1E20",
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                borderRadius: "4px",
                                padding: "1.5rem",
                                mt: "0.25rem",
                            }}
                        >
                            <Typography
                                sx={{
                                    marginBottom: "1.25rem",
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "#999999",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Cumulative days earned
                            </Typography>
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart
                                    data={trendData}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="referralDaysGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#E8A33D" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#E8A33D" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                    <XAxis
                                        dataKey="month"
                                        stroke="#999999"
                                        style={{ fontSize: "0.75rem" }}
                                    />
                                    <YAxis
                                        stroke="#999999"
                                        style={{ fontSize: "0.75rem" }}
                                        domain={[0, "dataMax + 30"]}
                                        allowDecimals={false}
                                    />
                                    <Tooltip content={<ReferralTrendTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="days"
                                        stroke="#E8A33D"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#referralDaysGradient)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    </ReferralSection>
                ) : null}

                {/* Invite History — hidden entirely when there are no invites */}
                {history.length > 0 ? (
                    <ReferralSection id="referral-invite-history" title="Invite History">
                        <Box>
                            <Box sx={{ display: "flex", gap: "0.75rem" }}>
                                <Box sx={{ flex: 2, minWidth: 0 }}>
                                    <Typography sx={REFERRAL_TABLE_HEAD_CELL_SX}>Friend</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={REFERRAL_TABLE_HEAD_CELL_SX}>Date</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={REFERRAL_TABLE_HEAD_CELL_SX}>Status</Typography>
                                </Box>
                                <Box sx={{ flex: "0 0 4.5rem", textAlign: "right" }}>
                                    <Typography sx={REFERRAL_TABLE_HEAD_CELL_SX}>Reward</Typography>
                                </Box>
                            </Box>
                            {history.map((row) => {
                                const status = getInviteStatus(row);
                                const isSubscribed = status.key === "subscribed";
                                return (
                                    <Box key={row.referredUserId} sx={REFERRAL_TABLE_ROW_SX}>
                                        <Box sx={{ flex: 2, minWidth: 0 }}>
                                            <Typography
                                                noWrap
                                                sx={{ color: colors.text.primary, fontSize: "0.85rem", fontWeight: 500 }}
                                            >
                                                {row.nickname}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                noWrap
                                                sx={{ color: colors.text.muted, fontSize: "0.78rem", fontWeight: 500 }}
                                            >
                                                {formatRelativeTime(row.referredAt)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    ...REFERRAL_INVITE_CHIP_BASE_SX,
                                                    ...(isSubscribed
                                                        ? {
                                                            borderColor: "rgba(34,197,94,0.28)",
                                                            background: "rgba(34,197,94,0.14)",
                                                            color: colors.state.success.main,
                                                        }
                                                        : {
                                                            borderColor: "rgba(232,163,61,0.28)",
                                                            background: "rgba(232,163,61,0.10)",
                                                            color: colors.primary.main,
                                                        }),
                                                }}
                                            >
                                                <Box className="dot" aria-hidden />
                                                {status.label}
                                            </Box>
                                        </Box>
                                        <Box sx={{ flex: "0 0 4.5rem", textAlign: "right" }}>
                                            <Typography
                                                sx={{
                                                    color: isSubscribed ? colors.accent.main : colors.text.muted,
                                                    fontSize: "0.8rem",
                                                    fontWeight: 700,
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {isSubscribed ? `+${row.creditDays || 30}d` : "—"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </ReferralSection>
                ) : null}

                {/* How It Works */}
                <ReferralSection id="referral-how-it-works" title="How it works">
                    <Box>
                        {[
                            {
                                n: "01",
                                title: "Share your link",
                                copy: "Send your referral code or personal link to friends you want to bring to Ouro.",
                            },
                            {
                                n: "02",
                                title: "Your friend signs up",
                                copy: "They create an account using your referral code during registration.",
                            },
                            {
                                n: "03",
                                title: "Get rewarded",
                                copy: "When they subscribe to Premium, a free month is automatically added to your account.",
                            },
                        ].map((step) => (
                            <Box key={step.n} sx={REFERRAL_STEP_ROW_SX}>
                                <Typography
                                    sx={{
                                        flexShrink: 0,
                                        width: "1.9rem",
                                        color: "rgba(232,163,61,0.45)",
                                        fontSize: "1.15rem",
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {step.n}
                                </Typography>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: colors.text.primary,
                                            fontSize: "0.92rem",
                                            fontWeight: 600,
                                            mb: "0.2rem",
                                        }}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: colors.text.secondary,
                                            fontSize: "0.8rem",
                                            fontWeight: 400,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {step.copy}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </ReferralSection>
            </Box>
        </Box>
    );
}

ReferralProgramPage.propTypes = {
    membership: PropTypes.object,
};

export default React.memo(ReferralProgramPage);
